/**
 * What staff read about a registration that failed or was cancelled, and about a refused registry
 * command: Vietnamese sentences, the registry's or the connection's own text only ever the folded
 * technical detail. Plain functions (type-only imports) so node tests can load them.
 */

import type { TFunction } from 'i18next';
import type { RegistryTransaction, StoppedRegistration } from '../../api';
import type { Tone } from '../../components/Badge';
import type { ErrorText } from '../../lib/apiErrorText';

/** Failure code of a registration staff cancelled ("Huỷ đăng ký"). */
const CANCELLED_BY_STAFF = 'CANCELLED_BY_STAFF';

/**
 * The "Cần xử lý" item of a failed registration; `null` when it did not fail. A staff
 * cancellation is a decision, not a failure: the server's reason says so (with why).
 */
export function registrationItem(
  registration: Pick<StoppedRegistration, 'state' | 'failure_code' | 'reason'> | null | undefined,
  t: TFunction<'domains'>,
): { tone: Tone; text: string } | null {
  if (registration?.state !== 'FAILED') return null;
  if (registration.failure_code === CANCELLED_BY_STAFF) return { tone: 'info', text: registration.reason };
  return { tone: 'serious', text: t('overview.registrationFailed', { reason: registration.reason }) };
}

/**
 * "Huỷ đăng ký": the dialog's body and the message once done (`domains:` keys). The customer's
 * funds are mentioned only when a hold is active (a free registration holds none).
 */
export function cancelTextKeys(held: StoppedRegistration['held'] | undefined) {
  return held?.status === 'ACTIVE'
    ? ({ body: 'stop.cancelBody', done: 'stop.cancelled' } as const)
    : ({ body: 'stop.cancelBodyNothingHeld', done: 'stop.cancelledNothingHeld' } as const);
}

/** A refused registry command, as far as its sentence goes. */
export interface RefusedCommandText {
  result_code: number | null;
  /** The registry's answer, or the connection's own sentence (English). */
  result_message: string | null;
  /** Refused by the connection before anything reached the registry. */
  not_sent: boolean;
  /** The server's Vietnamese explanation of a known refusal. */
  explanation: string | null;
}

/**
 * A refused command of the domain (`tx`) as [`refusalText`] reads it: the registration's refused
 * command when it is that one (with the server's explanation); else not sent when the registry
 * gave it no svTRID and the connection recorded "not sent: …" — the server's own `not_sent`
 * (registration_recovery.rs).
 */
export function refusedCommand(
  tx: Pick<RegistryTransaction, 'id' | 'svtrid' | 'result_code' | 'result_message'>,
  known: StoppedRegistration['refused_command'] | undefined,
): RefusedCommandText {
  if (known?.transaction_id === tx.id) return known;
  const not_sent = tx.svtrid === null && Boolean(tx.result_message?.startsWith('not sent'));
  return { result_code: tx.result_code, result_message: tx.result_message, not_sent, explanation: null };
}

/**
 * The sentence for a refused command: the server's explanation, else the kind of refusal (not
 * sent, or refused by the registry — its result code is shown beside) read by `read`, lib/
 * errorText's `errorText`. The command's own text is the `detail` (none when it is already the
 * text: a message written in Vietnamese). `said`: a sentence shown above it that states the
 * refusal (a stopped registration's reason): the text is then not repeated.
 */
export function refusalText(command: RefusedCommandText, read: (error: unknown) => ErrorText, said?: string): { text: string | null; detail: string | null } {
  const message = command.result_message?.trim() || null;
  if (command.explanation) return { text: said?.includes(command.explanation) ? null : command.explanation, detail: message };
  if (said !== undefined) return { text: null, detail: message };
  return read({ code: command.not_sent ? 'REGISTRY_NOT_SENT' : 'REGISTRY_REJECTED', status: -1, message: message ?? '' });
}

/**
 * The marker of a domain timeline event: a refusal or failure critical, a stop for staff (the
 * registration's manual review) a warning.
 */
export function eventTone(type: string): Tone {
  if (/(FAILED|REJECTED|REFUSED|BLOCKED)$/.test(type)) return 'crit';
  if (/UNKNOWN/.test(type)) return 'serious';
  if (/(SUSPENDED|EXPIRED|LOCKED|GRACE|REDEMPTION)/.test(type) || /STOPPED$/.test(type)) return 'warn';
  if (/(REQUESTED|SUBMITTED|STARTED)$/.test(type)) return 'pending';
  if (/(ACTIVATED|CREATED|RENEWED|ACKNOWLEDGED|CHANGED|COMPLETED)/.test(type)) return 'ok';
  return 'info';
}

/**
 * "Lệnh bị từ chối gần nhất" on the Registry tab: the latest refused command. A domain create the
 * registration closed without sending (`error_class` ABANDONED: the registration failed or was
 * cancelled before it) is no refusal.
 */
export function latestRefusal<T extends Pick<RegistryTransaction, 'state' | 'error_class' | 'started_at'>>(items: readonly T[]): T | undefined {
  return items
    .filter((x) => x.state === 'REJECTED' && x.error_class !== 'ABANDONED')
    .sort((a, b) => b.started_at.localeCompare(a.started_at))[0];
}
